import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { Button } from "../components/kit";
import api from "../lib/api";

export default function CoachReport() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/coach/overview").then((r) => setData(r.data)).catch(() => setError("Could not load the report."));
  }, []);

  if (error) return <div style={{ padding: 40, fontFamily: "var(--font-text)" }}>{error}</div>;
  if (!data) return <div style={{ padding: 40, fontFamily: "var(--font-text)" }}>Preparing report…</div>;

  const b = data.benchmarks;
  const today = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="buktu-root" data-testid="coach-report-page" style={{ minHeight: "100vh", background: "#fff" }}>
      <div className="report-toolbar" style={{ position: "sticky", top: 0, display: "flex", gap: 12, padding: "16px 24px", borderBottom: "1px solid var(--border-hairline)", background: "var(--surface-page)" }}>
        <Button variant="secondary" size="sm" iconLeft={<ArrowLeft size={15} />} onClick={() => navigate("/coach")}>Back</Button>
        <Button size="sm" iconLeft={<Printer size={15} />} data-testid="report-print-btn" onClick={() => window.print()}>Print / Save as PDF</Button>
      </div>

      <div style={{ maxWidth: 760, margin: "0 auto", padding: "40px 32px", fontFamily: "var(--font-text)", color: "var(--text-body)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "3px solid var(--buktu-green)", paddingBottom: 16, marginBottom: 24 }}>
          <div>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 26, fontWeight: 700, color: "var(--buktu-green)", letterSpacing: "-0.03em" }}>buktu · catalyst</div>
            <div style={{ font: "var(--type-body-sm)", color: "var(--text-muted)" }}>Cohort report</div>
          </div>
          <div style={{ textAlign: "right", font: "var(--type-body-sm)", color: "var(--text-muted)" }}>{today}</div>
        </div>

        <h1 style={{ font: "var(--type-heading)", fontSize: 24, marginBottom: 4 }}>{data.cohort_name}</h1>
        <p style={{ font: "var(--type-body)", color: "var(--text-muted)", marginBottom: 28 }}>
          {b.cohort_size} businesses · {b.scored_count} with a baseline · coached by {data.coach_name || "you"}
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 32 }}>
          {[["Average", b.avg_overall], ["Median", b.median], ["Top", b.top], ["At risk", b.at_risk_count]].map(([l, v]) => (
            <div key={l} style={{ border: "1px solid var(--border-hairline)", borderRadius: 12, padding: 16 }}>
              <div style={{ font: "var(--type-eyebrow)", color: "var(--text-faint)", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 11 }}>{l}</div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 28, fontWeight: 700, color: "var(--text-strong)" }}>{v}</div>
            </div>
          ))}
        </div>

        <h2 style={{ font: "var(--type-heading)", fontSize: 18, marginBottom: 12 }}>Cohort by dimension</h2>
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 32 }}>
          <tbody>
            {data.dimensions_order.map((dim) => (
              <tr key={dim} style={{ borderBottom: "1px solid var(--border-hairline)" }}>
                <td style={{ padding: "8px 0", font: "var(--type-body-sm)" }}>{dim}</td>
                <td style={{ padding: "8px 0", width: 220 }}>
                  <div style={{ height: 8, borderRadius: 999, background: "var(--ink-100)" }}>
                    <div style={{ width: (b.dimension_avgs[dim] || 0) + "%", height: "100%", borderRadius: 999, background: "var(--buktu-green)" }} />
                  </div>
                </td>
                <td style={{ padding: "8px 0 8px 12px", textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 13, width: 40 }}>{b.dimension_avgs[dim] || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h2 style={{ font: "var(--type-heading)", fontSize: 18, marginBottom: 12 }}>Members</h2>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "2px solid var(--border-strong)" }}>
              <th style={{ padding: "6px 4px" }}>Name</th><th style={{ padding: "6px 4px" }}>Shared</th>
              <th style={{ padding: "6px 4px" }}>Score</th><th style={{ padding: "6px 4px" }}>Streak</th><th style={{ padding: "6px 4px" }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.roster.map((m) => (
              <tr key={m.id} style={{ borderBottom: "1px solid var(--border-hairline)" }}>
                <td style={{ padding: "6px 4px" }}>{m.name}</td>
                <td style={{ padding: "6px 4px", color: "var(--text-muted)" }}>{m.shared_count} of {m.shared_total}</td>
                <td style={{ padding: "6px 4px", fontFamily: "var(--font-mono)" }}>{m.overall ?? "—"}</td>
                <td style={{ padding: "6px 4px" }}>{m.streak || 0}</td>
                <td style={{ padding: "6px 4px", textTransform: "capitalize" }}>{m.status.replace("_", " ")}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <p style={{ marginTop: 40, font: "var(--type-body-sm)", fontSize: 11, color: "var(--text-faint)" }}>
          Generated by Buktu Catalyst · self-reported baselines. For coaching use only.
        </p>
      </div>
      <style>{`@media print { .report-toolbar { display: none !important; } .buktu-root { background: #fff !important; } }`}</style>
    </div>
  );
}
